import { getBrowserStorage } from "./browserStorage";
import { APP_VERSION, BUILD_CHANNEL, TELEMETRY_EVENT_VERSION } from "./buildInfo";
import {
  captureMonitoringError,
  trackProductAnalyticsEvent,
  updateMonitoringConfig,
  updateMonitoringContext
} from "./monitoring";
import { invokeSupabaseFunction } from "./supabase";

export type AppTelemetryPayload = Record<string, string | number | boolean | null>;

type TelemetryContext = {
  anonymousUserId: string | null;
  errorMonitoringEnabled: boolean;
  feedbackSurveyEnabled: boolean;
  inviteCohort: string | null;
  productAnalyticsEnabled: boolean;
  runId: string | null;
  screen: string;
  sessionReplayEnabled: boolean;
  telemetryEnabled: boolean;
};

type TelemetryEnvelope = {
  appVersion: string;
  buildChannel: string;
  eventName: string;
  eventVersion: number;
  payload: AppTelemetryPayload;
  runId: string | null;
  screen: string;
  sessionId: string;
};

const SESSION_STORAGE_KEY = "tavela.sessionId";
const PRODUCT_ANALYTICS_EVENT_NAMES = new Set([
  "app_loaded",
  "beta_gate_failed",
  "beta_gate_passed",
  "beta_gate_viewed",
  "feedback_submitted",
  "first_round_cleared",
  "leaderboard_save_failed",
  "leaderboard_save_succeeded",
  "run_ended",
  "run_started",
  "screen_viewed",
  "support_link_clicked",
  "tutorial_progress"
]);

function createSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId(): string {
  const storage = getBrowserStorage("session");
  if (storage === null) {
    return createSessionId();
  }

  try {
    const existingValue = storage.getItem(SESSION_STORAGE_KEY);
    if (existingValue && existingValue.trim().length > 0) {
      return existingValue;
    }

    const nextValue = createSessionId();
    storage.setItem(SESSION_STORAGE_KEY, nextValue);
    return nextValue;
  } catch {
    return createSessionId();
  }
}

function sanitizePayload(payload: AppTelemetryPayload): AppTelemetryPayload {
  return Object.entries(payload).reduce<AppTelemetryPayload>((nextPayload, [key, value]) => {
    if (value === undefined) {
      return nextPayload;
    }

    nextPayload[key] = value;
    return nextPayload;
  }, {});
}

export class TelemetryClient {
  private readonly sessionId = getSessionId();
  private context: TelemetryContext = {
    anonymousUserId: null,
    errorMonitoringEnabled: false,
    feedbackSurveyEnabled: false,
    inviteCohort: null,
    productAnalyticsEnabled: false,
    runId: null,
    screen: "title",
    sessionReplayEnabled: false,
    telemetryEnabled: true
  };
  private sendQueue = Promise.resolve();

  constructor() {
    updateMonitoringContext({
      screen: this.context.screen,
      sessionId: this.sessionId
    });
  }

  getSessionId(): string {
    return this.sessionId;
  }

  updateContext(partialContext: Partial<TelemetryContext>): void {
    this.context = {
      ...this.context,
      ...partialContext
    };

    updateMonitoringConfig({
      errorMonitoringEnabled: this.context.errorMonitoringEnabled,
      feedbackSurveyEnabled: this.context.feedbackSurveyEnabled,
      productAnalyticsEnabled: this.context.productAnalyticsEnabled,
      sessionReplayEnabled: this.context.sessionReplayEnabled,
      telemetryEnabled: this.context.telemetryEnabled
    });
    updateMonitoringContext({
      anonymousUserId: this.context.anonymousUserId,
      inviteCohort: this.context.inviteCohort,
      runId: this.context.runId,
      screen: this.context.screen,
      sessionId: this.sessionId
    });
  }

  track(
    eventName: string,
    payload: AppTelemetryPayload = {},
    contextOverride: Partial<TelemetryContext> = {}
  ): void {
    const effectiveContext = {
      ...this.context,
      ...contextOverride
    };
    const envelope: TelemetryEnvelope = {
      appVersion: APP_VERSION,
      buildChannel: BUILD_CHANNEL,
      eventName,
      eventVersion: TELEMETRY_EVENT_VERSION,
      payload: sanitizePayload(payload),
      runId: effectiveContext.runId,
      screen: effectiveContext.screen,
      sessionId: this.sessionId
    };

    if (!effectiveContext.telemetryEnabled) {
      if (import.meta.env.DEV) {
        console.info("[telemetry-disabled]", eventName, envelope.payload);
      }
      return;
    }

    if (PRODUCT_ANALYTICS_EVENT_NAMES.has(eventName)) {
      trackProductAnalyticsEvent(eventName, {
        anonymous_user_id: effectiveContext.anonymousUserId,
        app_version: envelope.appVersion,
        build_channel: envelope.buildChannel,
        event_version: envelope.eventVersion,
        invite_cohort: effectiveContext.inviteCohort,
        run_id: envelope.runId,
        screen: envelope.screen,
        session_id: envelope.sessionId,
        ...envelope.payload
      });
    }

    this.sendQueue = this.sendQueue
      .then(async () => {
        await invokeSupabaseFunction("collect-beta-event", envelope, {
          shouldTrace: false
        });
      })
      .catch((error: unknown) => {
        if (import.meta.env.DEV) {
          console.info("[telemetry-send-failed]", {
            eventName,
            message: error instanceof Error ? error.message : "Unknown error"
          });
        }
      });
  }

  captureError(
    source: string,
    error: unknown,
    details: AppTelemetryPayload = {},
    options: Partial<{
      forwardToSentry: boolean;
    }> = {}
  ): void {
    const message =
      error instanceof Error ? error.message : typeof error === "string" ? error : "Unknown error";
    this.track("client_error", {
      source,
      message,
      ...details
    });
    captureMonitoringError(source, error, {
      message,
      ...details
    }, {
      forwardToSentry: options.forwardToSentry
    });
  }
}

export function shouldSendEventToProductAnalytics(eventName: string): boolean {
  return PRODUCT_ANALYTICS_EVENT_NAMES.has(eventName);
}
